/**
 * DevDebugPanel — developer debug panel showing real-time voice session events.
 *
 * Shows tool calls with content tiers, slow mode verdicts, moderation blocks,
 * kid transcripts, and agent responses. Color-coded by tier/severity.
 *
 * Only renders when debug mode is enabled (__DEV__ or ?dev=1 URL param).
 */

import React, { useEffect, useRef } from "react";
import { View, Text, ScrollView, Pressable, StyleSheet, Platform } from "react-native";
import { useDebugStore, DebugEvent, DebugEventType } from "../../store/debugStore";

// Color coding
const TIER_COLORS: Record<string, string> = {
  ALLOW: "#34D399",
  SANITIZE: "#FBBF24",
  INFO: "#60A5FA",
  PIVOT: "#F97316",
  REFUSE: "#EF4444",
};

const EVENT_COLORS: Record<DebugEventType, string> = {
  toolUse: "#A78BFA",
  toolResult: "#6EE7B7",
  moderationBlocked: "#EF4444",
  slowModeVerdict: "#F59E0B",
  ipSanitizer: "#8B5CF6",
  kidTranscript: "#93C5FD",
  agentResponse: "#D1D5DB",
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }) +
    "." + String(d.getMilliseconds()).padStart(3, "0");
}

function TierBadge({ tier }: { tier?: string }) {
  if (!tier) return null;
  const color = TIER_COLORS[tier] || "#9CA3AF";
  return (
    <View style={[s.badge, { backgroundColor: color + "33", borderColor: color }]}>
      <Text style={[s.badgeText, { color }]}>{tier}</Text>
    </View>
  );
}

function SlowModeBadge({ isAppropriate }: { isAppropriate?: boolean }) {
  if (isAppropriate === undefined) return null;
  const color = isAppropriate ? "#34D399" : "#EF4444";
  const label = isAppropriate ? "PASS" : "BLOCK";
  return (
    <View style={[s.badge, { backgroundColor: color + "33", borderColor: color }]}>
      <Text style={[s.badgeText, { color }]}>{label}</Text>
    </View>
  );
}

function EventRow({ event }: { event: DebugEvent }) {
  const borderColor = EVENT_COLORS[event.type] || "#6B7280";

  switch (event.type) {
    case "toolUse":
      return (
        <View style={[s.eventRow, { borderLeftColor: borderColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={s.eventLabel}>TOOL</Text>
              <Text style={s.toolName}>{event.toolName || "unknown"}</Text>
              <TierBadge tier={event.contentTier} />
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            {event.action && <Text style={s.eventDetail}>action: {event.action}</Text>}
            {event.topics && event.topics.length > 0 && (
              <Text style={s.eventDetail}>topics: {event.topics.join(", ")}</Text>
            )}
          </View>
        </View>
      );

    case "toolResult":
      return (
        <View style={[s.eventRow, { borderLeftColor: borderColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={s.eventLabel}>RESULT</Text>
              {event.error && <Text style={s.eventDetail}>{event.error}</Text>}
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            {event.message && <Text style={s.eventDetail}>{event.message}</Text>}
          </View>
        </View>
      );

    case "slowModeVerdict":
      return (
        <View style={[s.eventRow, { borderLeftColor: borderColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={s.eventLabel}>SLOW MODE</Text>
              <SlowModeBadge isAppropriate={event.isAppropriate} />
              {event.latencyMs !== undefined && (
                <Text style={s.latency}>{event.latencyMs}ms</Text>
              )}
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            {event.reason && <Text style={s.eventDetail}>{event.reason}</Text>}
          </View>
        </View>
      );

    case "ipSanitizer": {
      const tierColor = event.verdict === "BLOCK" ? "#EF4444" : event.verdict === "SANITIZE" ? "#F59E0B" : "#34D399";
      return (
        <View style={[s.eventRow, { borderLeftColor: tierColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={s.eventLabel}>IP SANITIZER</Text>
              <View style={[s.badge, { backgroundColor: tierColor + "33", borderColor: tierColor }]}>
                <Text style={[s.badgeText, { color: tierColor }]}>{event.verdict}</Text>
              </View>
              {event.latencyMs !== undefined && (
                <Text style={s.latency}>{event.latencyMs}ms</Text>
              )}
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            {event.reason && <Text style={s.eventDetail}>{event.reason}</Text>}
          </View>
        </View>
      );
    }

    case "moderationBlocked":
      return (
        <View style={[s.eventRow, { borderLeftColor: borderColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={[s.eventLabel, { color: "#EF4444" }]}>BLOCKED</Text>
              <Text style={s.eventDetail}>{event.elementType} / {event.stage}</Text>
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            {event.reason && <Text style={s.eventDetail}>{event.reason}</Text>}
          </View>
        </View>
      );

    case "kidTranscript":
      return (
        <View style={[s.eventRow, { borderLeftColor: borderColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={s.eventLabel}>KID</Text>
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            <Text style={s.transcript} numberOfLines={2}>{event.text}</Text>
          </View>
        </View>
      );

    case "agentResponse":
      return (
        <View style={[s.eventRow, { borderLeftColor: borderColor }]}>
          <View style={s.eventContent}>
            <View style={s.eventHeader}>
              <Text style={s.eventLabel}>AGENT</Text>
              <Text style={s.eventTimeRight}>{formatTime(event.timestamp)}</Text>
            </View>
            <Text style={s.transcript} numberOfLines={2}>{event.text}</Text>
          </View>
        </View>
      );

    default:
      return null;
  }
}

export default function DevDebugPanel() {
  const { panelOpen, events, togglePanel, clear } = useDebugStore();
  const scrollRef = useRef<ScrollView>(null);

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (panelOpen && scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollToEnd({ animated: true });
      }, 50);
    }
  }, [events.length, panelOpen]);

  // Keyboard shortcut (Escape to close)
  useEffect(() => {
    if (Platform.OS !== "web" || !panelOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") togglePanel();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [panelOpen]);

  if (!panelOpen) return null;

  return (
    <View style={s.container}>
      {/* Header */}
      <View style={s.header}>
        <Text style={s.title}>Debug Events ({events.length})</Text>
        <View style={s.headerActions}>
          <Pressable style={s.headerBtn} onPress={clear}>
            <Text style={s.headerBtnText}>Clear</Text>
          </Pressable>
          <Pressable style={s.headerBtn} onPress={togglePanel}>
            <Text style={s.headerBtnText}>Close</Text>
          </Pressable>
        </View>
      </View>

      {/* Event list */}
      <ScrollView ref={scrollRef} style={s.scrollArea} contentContainerStyle={s.scrollContent}>
        {events.length === 0 ? (
          <Text style={s.empty}>No events yet. Start a voice session to see events.</Text>
        ) : (
          events.map((event) => <EventRow key={event.id} event={event} />)
        )}
      </ScrollView>
    </View>
  );
}

const s = StyleSheet.create({
  container: {
    position: "absolute",
    bottom: 16,
    right: 16,
    width: 420,
    maxHeight: 500,
    backgroundColor: "#0F1120",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#2D2F45",
    overflow: "hidden",
    zIndex: 9999,
    ...(Platform.OS === "web" ? { boxShadow: "0 8px 32px rgba(0,0,0,0.5)" } : {}),
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#2D2F45",
    backgroundColor: "#161830",
  },
  title: {
    fontSize: 12,
    fontWeight: "700",
    color: "#E5E7EB",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  headerActions: {
    flexDirection: "row",
    gap: 8,
  },
  headerBtn: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: "#2D2F45",
  },
  headerBtnText: {
    fontSize: 11,
    color: "#9CA3AF",
    fontWeight: "600",
  },
  scrollArea: {
    flex: 1,
    maxHeight: 450,
  },
  scrollContent: {
    paddingVertical: 4,
  },
  empty: {
    padding: 16,
    fontSize: 12,
    color: "#6B7280",
    textAlign: "center",
    fontStyle: "italic",
  },
  eventRow: {
    flexDirection: "row",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderLeftWidth: 3,
    marginLeft: 4,
    marginBottom: 2,
  },
  eventTime: {
    fontSize: 10,
    color: "#6B7280",
    width: 72,
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  eventContent: {
    flex: 1,
  },
  eventTimeRight: {
    fontSize: 10,
    color: "#6B7280",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
    marginLeft: "auto",
  },
  eventHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  eventLabel: {
    fontSize: 10,
    fontWeight: "700",
    color: "#9CA3AF",
    letterSpacing: 0.5,
  },
  toolName: {
    fontSize: 11,
    color: "#E5E7EB",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  eventDetail: {
    fontSize: 10,
    color: "#9CA3AF",
    marginTop: 1,
  },
  transcript: {
    fontSize: 11,
    color: "#D1D5DB",
    marginTop: 1,
  },
  latency: {
    fontSize: 10,
    color: "#6B7280",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  badge: {
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 3,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
});
